/**
 * The only module that fetches car content.
 *
 * The catalogue is served as static JSON from hosting rather than bundled, for
 * the reason `src/domain/i18n/messages/index.js` gives: the shell's vocabulary
 * is a few hundred bytes and must never flash, while car content — twelve
 * descriptions, thirty-six fun facts — is the payload actually worth splitting.
 *
 * Two files per load, by design: the figures are language-independent and stay
 * cached across a language switch, the prose is not.
 */

const CONTENT_BASE = '/assets/content/cars';
const REQUEST_TIMEOUT_MS = 10000;

/**
 * Once more, and then give up.
 *
 * The repo's rule for anything outbound is a timeout and a retry, and both
 * halves earn their place here: a phone leaving a lift drops exactly one
 * request, and the whole garage is behind this fetch. A second attempt is the
 * difference between a stumble and an error screen. A third would be a spinner
 * that will not end.
 */

/**
 * `fetch` and the timeout arrive as defaulted parameters so this is testable
 * without a network and safe to import outside a browser.
 */
async function fetchJsonOnce(url, { fetchImpl = globalThis.fetch, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

export async function fetchJson(url, options = {}) {
    try {
        return await fetchJsonOnce(url, options);
    } catch (error) {
        // Sequential on purpose, and written as a second call rather than a
        // loop: the retry only means anything once the first attempt has
        // actually failed, and two requests racing at a dead connection are
        // still two requests at a dead connection.
        return fetchJsonOnce(url, options);
    }
}

export async function fetchCatalogue(options = {}) {
    const payload = await fetchJson(`${CONTENT_BASE}/catalogue.json`, options);

    return payload.cars || [];
}

/**
 * Prose for a language, and English underneath it.
 *
 * Only `en` is authored today; anything else 404s. That is an expected state,
 * not an error — a missing translation shows English prose, never an empty card
 * — so a failed language fetch resolves to `{}` and lets the English layer show
 * through. The English fetch itself is not caught: without it there is no
 * catalogue to render, and the screen has to say so.
 */
export async function fetchProse(languageCode, options = {}) {
    const english = await fetchJson(`${CONTENT_BASE}/prose.en.json`, options);

    if (languageCode === 'en') {
        return { prose: english, fallbackProse: english };
    }

    try {
        return {
            prose: await fetchJson(`${CONTENT_BASE}/prose.${languageCode}.json`, options),
            fallbackProse: english,
        };
    } catch {
        return { prose: {}, fallbackProse: english };
    }
}

export { CONTENT_BASE };
